import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { Gamepad2, RefreshCw, Copy, Check, X, Filter, Eye } from 'lucide-react';
import CursorPagination from 'components/CursorPagination';
import { appendCursorParams, parseCursorPagination } from 'lib/cursor-pagination';
import { useCursorListPage } from 'hooks/useCursorListPage';
import { DEFAULT_PAGE_SIZE } from 'lib/page-size-options';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';
import {
  BATTLE_STATUS,
  BATTLE_STATUS_LABEL,
  BATTLE_TYPE,
  BATTLE_TYPE_LABEL,
  labelFor,
  statusPillByLabel,
} from 'constants/enums';
import type { Variant } from 'types/entities';

interface BattleFilters {
  userId: string;
  mode: string;
  status: string;
  type: string;
  battleId: string;
  username: string;
  uuid: string;
  dateFrom: string;
  dateTo: string;
  privateTableId: string;
  consumerDbId: string;
}

const EMPTY_BATTLE_FILTERS: BattleFilters = {
  userId: '',
  mode: '',
  status: '',
  type: '',
  battleId: '',
  username: '',
  uuid: '',
  dateFrom: '',
  dateTo: '',
  privateTableId: '',
  consumerDbId: '',
};

const BATTLE_STATUS_OPTIONS: [string, string][] = [
  ['', 'All statuses'],
  ...Object.entries(BATTLE_STATUS_LABEL)
    .filter(([code]) => Number(code) !== BATTLE_STATUS.ACTIVE)
    .map(([code, label]) => [code, label] as [string, string]),
];
const BATTLE_TYPE_OPTIONS: [string, string][] = [
  ['', 'All types'],
  ...Object.entries(BATTLE_TYPE_LABEL).map(([code, label]) => [code, label] as [string, string]),
];

const battleTypePill = (code: number | string) =>
  Number(code) === BATTLE_TYPE.PRIVATE
    ? 'bg-amber-500/10 text-amber-700 border-amber-200'
    : 'bg-subtle text-muted border-line';

const battleStatusPill = (code: number | string) => statusPillByLabel(labelFor(BATTLE_STATUS_LABEL, code));

function buildBattleQueryParams(filters: BattleFilters) {
  const params = new URLSearchParams();
  if (filters.userId.trim()) {
    params.set('userId', filters.userId.trim());
  } else if (filters.username.trim()) {
    params.set('username', filters.username.trim());
  }
  if (filters.status) params.set('status', filters.status);
  if (filters.type) params.set('type', filters.type);
  if (filters.mode) params.set('variantId', filters.mode);
  if (filters.battleId.trim()) params.set('battleId', filters.battleId.trim());
  if (filters.uuid.trim()) params.set('uuid', filters.uuid.trim());
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.privateTableId.trim()) params.set('privateTableId', filters.privateTableId.trim());
  if (filters.consumerDbId.trim()) params.set('consumerDbId', filters.consumerDbId.trim());
  return params;
}

function countActiveBattleFilters(filters: BattleFilters): number {
  let n = 0;
  if (filters.userId.trim() || filters.username.trim()) n += 1;
  if (filters.mode) n += 1;
  if (filters.status) n += 1;
  if (filters.type) n += 1;
  if (filters.battleId.trim()) n += 1;
  if (filters.uuid.trim()) n += 1;
  if (filters.dateFrom) n += 1;
  if (filters.dateTo) n += 1;
  if (filters.privateTableId.trim()) n += 1;
  if (filters.consumerDbId.trim()) n += 1;
  return n;
}

function battleFiltersFromPlayerUrl(search: string): BattleFilters | null {
  const params = new URLSearchParams(search);
  const userId = params.get('userId') || '';
  if (!userId) return null;
  return {
    ...EMPTY_BATTLE_FILTERS,
    userId,
    username: params.get('username') || '',
  };
}

function battleFiltersFromPrivateTableUrl(search: string): BattleFilters | null {
  const params = new URLSearchParams(search);
  const privateTableId = params.get('privateTableId') || '';
  if (!privateTableId) return null;
  return {
    ...EMPTY_BATTLE_FILTERS,
    privateTableId,
    type: params.get('type') || '2',
  };
}

function battleFiltersFromConsumerUrl(search: string): BattleFilters | null {
  const params = new URLSearchParams(search);
  const consumerDbId = params.get('consumerDbId') || '';
  if (!consumerDbId) return null;
  return {
    ...EMPTY_BATTLE_FILTERS,
    consumerDbId,
  };
}

function BattleUuidCell({ uuid }: { uuid?: string | null }) {
  const [copied, setCopied] = useState(false);
  const full = uuid?.trim() || '—';
  const canCopy = full !== '—';

  const copyId = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="flex items-start gap-1.5 min-w-[220px] max-w-[320px] mt-1">
      <span className="font-mono text-[11px] text-muted break-all leading-snug">{full}</span>
      {canCopy && (
        <button
          type="button"
          onClick={() => void copyId()}
          title={copied ? 'Copied' : 'Copy battle ID'}
          aria-label={copied ? 'Copied battle ID' : 'Copy battle ID'}
          className="shrink-0 p-1 rounded-md text-faint hover:text-heading hover:bg-subtle transition-colors"
        >
          {copied ? <Check size={14} strokeWidth={2.5} className="text-emerald-600" /> : <Copy size={14} strokeWidth={2} />}
        </button>
      )}
    </div>
  );
}

const GameMonitoring = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [draftBattleFilters, setDraftBattleFilters] = useState<BattleFilters>(EMPTY_BATTLE_FILTERS);
  const [appliedBattleFilters, setAppliedBattleFilters] = useState<BattleFilters>(EMPTY_BATTLE_FILTERS);
  const [playerFilterLabel, setPlayerFilterLabel] = useState('');
  const [privateTableFilterLabel, setPrivateTableFilterLabel] = useState('');
  const [consumerFilterLabel, setConsumerFilterLabel] = useState('');
  const [gameModes, setGameModes] = useState<Variant[]>([]);

  const fetchPage = useCallback(async (cursor: number | null) => {
    const params = appendCursorParams(buildBattleQueryParams(appliedBattleFilters), cursor, pageSize);
    const res = await api.get(`${API_ENDPOINTS.ADMIN.BATTLES.GET_ALL}?${params.toString()}`);
    const meta = parseCursorPagination(res);
    return {
      items: res.success && Array.isArray(res.data) ? (res.data as any[]) : [],
      nextCursor: meta.nextCursor,
      hasMore: meta.hasMore,
      total: meta.total,
      totalPages: meta.totalPages,
    };
  }, [appliedBattleFilters, pageSize]);

  const {
    page: battlePage,
    items: battles,
    loading: loadingBattles,
    hasMore: battlesHasMore,
    total: battlesTotal,
    totalPages: battlesTotalPages,
    limit: battleLimit,
    resetAndLoad,
    refreshCurrent,
    goFirst,
    goToPage,
    goLast,
  } = useCursorListPage(fetchPage, pageSize);

  useEffect(() => {
    api.get<Variant[]>(API_ENDPOINTS.ADMIN.VARIANTS.GET_ALL)
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setGameModes(res.data as Variant[]);
        }
      })
      .catch((err) => console.error('Failed to fetch game modes:', err));
  }, []);

  useEffect(() => {
    if (location.pathname !== '/battles') return;

    const playerFilters = battleFiltersFromPlayerUrl(location.search);
    if (playerFilters) {
      setDraftBattleFilters(playerFilters);
      setAppliedBattleFilters(playerFilters);
      setPlayerFilterLabel(playerFilters.username);
      setPrivateTableFilterLabel('');
      setConsumerFilterLabel('');
      return;
    }

    const privateTableFilters = battleFiltersFromPrivateTableUrl(location.search);
    if (privateTableFilters) {
      setDraftBattleFilters(privateTableFilters);
      setAppliedBattleFilters(privateTableFilters);
      const params = new URLSearchParams(location.search);
      const roomCode = params.get('roomCode') || '';
      setPrivateTableFilterLabel(
        roomCode ? `PT-${privateTableFilters.privateTableId} · ${roomCode}` : `PT-${privateTableFilters.privateTableId}`
      );
      setPlayerFilterLabel('');
      setConsumerFilterLabel('');
      return;
    }

    const consumerFilters = battleFiltersFromConsumerUrl(location.search);
    if (consumerFilters) {
      setDraftBattleFilters(consumerFilters);
      setAppliedBattleFilters(consumerFilters);
      setConsumerFilterLabel(`Consumer #${consumerFilters.consumerDbId}`);
      setPlayerFilterLabel('');
      setPrivateTableFilterLabel('');
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname !== '/battles') return;
    resetAndLoad();
  }, [appliedBattleFilters, pageSize, location.pathname, resetAndLoad]);

  const updateBattleDraft = (patch: Partial<BattleFilters>) => {
    setDraftBattleFilters((prev) => ({ ...prev, ...patch }));
  };

  const applyBattleFilters = () => {
    setAppliedBattleFilters(draftBattleFilters);
  };

  const clearBattleFilters = () => {
    setDraftBattleFilters(EMPTY_BATTLE_FILTERS);
    setAppliedBattleFilters(EMPTY_BATTLE_FILTERS);
    setPlayerFilterLabel('');
    setPrivateTableFilterLabel('');
    setConsumerFilterLabel('');
    navigate('/battles', { replace: true });
  };

  const clearPrivateTableFilter = () => {
    const next = { ...appliedBattleFilters, privateTableId: '', type: '' };
    setDraftBattleFilters((prev) => ({ ...prev, privateTableId: '', type: '' }));
    setAppliedBattleFilters(next);
    setPrivateTableFilterLabel('');
    navigate('/battles', { replace: true });
  };

  const clearConsumerFilter = () => {
    const next = { ...appliedBattleFilters, consumerDbId: '' };
    setDraftBattleFilters((prev) => ({ ...prev, consumerDbId: '' }));
    setAppliedBattleFilters(next);
    setConsumerFilterLabel('');
    navigate('/battles', { replace: true });
  };

  const clearPlayerFilter = () => {
    const next = { ...appliedBattleFilters, userId: '', username: '' };
    setDraftBattleFilters((prev) => ({ ...prev, userId: '', username: '' }));
    setAppliedBattleFilters(next);
    setPlayerFilterLabel('');
    navigate('/battles', { replace: true });
  };

  const openBattle = (id: number) => navigate(`/battles/${id}`);

  const activeBattleFilterCount = countActiveBattleFilters(appliedBattleFilters);

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="bg-card p-6 rounded-2xl border border-line flex items-center gap-3 shadow-sm">
        <div className="p-3 bg-indigo-500/10 text-indigo-600 rounded-xl"><Gamepad2 size={24} /></div>
        <div>
          <h3 className="text-xl font-bold text-heading">Battles</h3>
          <p className="text-sm text-muted">Live &amp; historical battles, rounds and actions.</p>
        </div>
      </div>

      <div className="bg-card rounded-2xl shadow-sm border border-line overflow-hidden">
          <div className={`${LIST_TD_CLASS} border-b border-line flex flex-col sm:flex-row sm:items-center justify-between gap-3`}>
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-indigo-600" />
              <h4 className="font-bold text-heading">Filters</h4>
              {activeBattleFilterCount > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xxs font-bold bg-indigo-500/10 text-indigo-700">
                  {activeBattleFilterCount} active
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={clearBattleFilters}
                disabled={activeBattleFilterCount === 0 && countActiveBattleFilters(draftBattleFilters) === 0}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-muted hover:text-heading border border-line hover:bg-subtle transition-colors disabled:opacity-50"
              >
                <X size={14} /> Clear
              </button>
              <button
                type="button"
                onClick={() => refreshCurrent()}
                className="p-2 rounded-lg text-muted hover:text-heading border border-line hover:bg-subtle transition-colors"
                title="Refresh"
              >
                <RefreshCw size={16} className={loadingBattles ? 'animate-spin' : ''} />
              </button>
              <button
                type="button"
                onClick={applyBattleFilters}
                className="px-4 py-2 bg-brand-dark text-white rounded-lg text-sm font-bold hover:bg-opacity-95"
              >
                Apply Filters
              </button>
            </div>
          </div>

          <div className={`${LIST_TD_CLASS} grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 border-b border-line bg-subtle/40`}>
            <div>
              <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">Mode</label>
              <select
                value={draftBattleFilters.mode}
                onChange={(e) => updateBattleDraft({ mode: e.target.value })}
                className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card"
              >
                <option value="">All modes</option>
                {gameModes.map((v) => (
                  <option key={v.id} value={String(v.id)}>{v.code}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">Player name</label>
              <input
                value={draftBattleFilters.username}
                onChange={(e) => updateBattleDraft({ username: e.target.value })}
                placeholder="Search player"
                className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card"
              />
            </div>
            <div>
              <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">Status</label>
              <select
                value={draftBattleFilters.status}
                onChange={(e) => updateBattleDraft({ status: e.target.value })}
                className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card"
              >
                {BATTLE_STATUS_OPTIONS.map(([code, label]) => <option key={code || 'all'} value={code}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">Type</label>
              <select
                value={draftBattleFilters.type}
                onChange={(e) => updateBattleDraft({ type: e.target.value })}
                className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card"
              >
                {BATTLE_TYPE_OPTIONS.map(([code, label]) => <option key={code || 'all'} value={code}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">Battle #</label>
              <input
                value={draftBattleFilters.battleId}
                onChange={(e) => updateBattleDraft({ battleId: e.target.value })}
                placeholder="e.g. 1042"
                className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card font-mono"
              />
            </div>
            <div>
              <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">Battle ID</label>
              <input
                value={draftBattleFilters.uuid}
                onChange={(e) => updateBattleDraft({ uuid: e.target.value })}
                placeholder="Partial ID"
                className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card font-mono"
              />
            </div>
            <div>
              <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">From date</label>
              <input
                type="date"
                value={draftBattleFilters.dateFrom}
                onChange={(e) => updateBattleDraft({ dateFrom: e.target.value })}
                className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card"
              />
            </div>
            <div>
              <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">To date</label>
              <input
                type="date"
                value={draftBattleFilters.dateTo}
                min={draftBattleFilters.dateFrom || undefined}
                onChange={(e) => updateBattleDraft({ dateTo: e.target.value })}
                className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card"
              />
            </div>
          </div>

          {appliedBattleFilters.consumerDbId && (
            <div className="mx-6 mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-violet-200/80 bg-violet-500/10 px-4 py-3">
              <p className="text-sm text-body">
                Showing battles for{' '}
                <span className="font-bold text-heading">{consumerFilterLabel || `Consumer #${appliedBattleFilters.consumerDbId}`}</span>
              </p>
              <button
                type="button"
                onClick={clearConsumerFilter}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line bg-card text-xs font-bold text-muted hover:text-heading hover:bg-subtle transition-colors shrink-0"
              >
                <X size={14} /> Clear consumer filter
              </button>
            </div>
          )}

          {appliedBattleFilters.privateTableId && (
            <div className="mx-6 mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-amber-200/80 bg-amber-500/10 px-4 py-3">
              <p className="text-sm text-body">
                Showing battles for private table{' '}
                <span className="font-bold text-heading">{privateTableFilterLabel || `PT-${appliedBattleFilters.privateTableId}`}</span>
              </p>
              <button
                type="button"
                onClick={clearPrivateTableFilter}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line bg-card text-xs font-bold text-muted hover:text-heading hover:bg-subtle transition-colors shrink-0"
              >
                <X size={14} /> Clear table filter
              </button>
            </div>
          )}

          {appliedBattleFilters.userId && (
            <div className="mx-6 mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-indigo-200/80 bg-indigo-500/10 px-4 py-3">
              <p className="text-sm text-body">
                Showing battles for{' '}
                <span className="font-bold text-heading">{playerFilterLabel || 'selected player'}</span>
              </p>
              <button
                type="button"
                onClick={clearPlayerFilter}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line bg-card text-xs font-bold text-muted hover:text-heading hover:bg-subtle transition-colors shrink-0"
              >
                <X size={14} /> Clear player filter
              </button>
            </div>
          )}

          <div className={`${LIST_TD_CLASS} border-b border-line`}>
            <h4 className="font-bold text-heading">Battle History</h4>
          </div>

          <div className="overflow-auto max-h-[62vh]">
            <table className={LIST_TABLE_CLASS}>
              <thead className={LIST_THEAD_CLASS}>
                <tr>
                  <th className={LIST_TH_NO_CLASS}>No.</th>
                  {['Battle', 'Type', 'Variant', 'Table', 'Players', 'Betting Rounds', 'Status', 'Action'].map((h, i) => (
                    <th key={i} className={LIST_TH_CLASS}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={LIST_TBODY_CLASS}>
                {battles.length > 0 ? battles.map((b, index) => (
                  <tr key={b.id} className="hover:bg-subtle/50 transition-colors">
                    <td className={LIST_TD_NO_CLASS}>{listRowNumber(battlePage, index, battleLimit)}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm`}>
                      <div className="font-bold text-heading">BTL-{b.id}</div>
                      <BattleUuidCell uuid={b.uuid} />
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full border ${battleTypePill(b.battle_type)}`}>{labelFor(BATTLE_TYPE_LABEL, b.battle_type)}</span>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>{b.variant_name || b.variant_code || '—'}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>
                      <div>{b.table_name || '—'}</div>
                      {b.room_code && <div className="text-[10px] font-mono text-muted">{b.room_code}</div>}
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>{b.player_count ?? b.active_players ?? 0}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>{b.total_rounds}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full border ${battleStatusPill(b.status)}`}>{labelFor(BATTLE_STATUS_LABEL, b.status)}</span>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm`}>
                      <button
                        type="button"
                        onClick={() => openBattle(b.id)}
                        title="View battle"
                        aria-label="View battle"
                        className="inline-flex items-center justify-center p-2 bg-subtle hover:bg-subtle text-heading border border-line rounded-lg transition-all"
                      >
                        <Eye size={16} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={9} className="px-6 py-10 text-center text-muted font-medium">
                    {loadingBattles ? 'Loading battles…' : 'No battles match the selected filters.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <CursorPagination
            page={battlePage}
            totalPages={battlesTotalPages}
            total={battlesTotal}
            itemCount={battles.length}
            limit={battleLimit}
            hasMore={battlesHasMore}
            loading={loadingBattles}
            onFirst={goFirst}
            onPageChange={goToPage}
            onLast={goLast}
            onLimitChange={setPageSize}
          />
      </div>
    </div>
  );
};

export default GameMonitoring;
